import React, { useState, useEffect } from 'react';
import { Route, Routes as ReactRoutes, Navigate } from "react-router-dom";

// Composants globaux
import Preloader from "../components/Preloader";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// Pages principales
import Presentation from "./Presentation";
import Signin from "./examples/Signin";
import Signup from "./examples/Signup";
import ForgotPassword from "./examples/ForgotPassword";
import ResetPassword from "./examples/ResetPassword";
import Lock from "./examples/Lock";
import NotFoundPage from "./examples/NotFound";
import ServerError from "./examples/ServerError";

// Pages de dashboard
import DashboardOverview from "./dashboard/DashboardOverview";
import Upgrade from "./Upgrade";
import Transactions from "./Transactions";
import Arrondissement from "./Arrondissement";
import Cities from "./Cities";
import Categories from "./Categories";
import AccessConfig from "./AccessConfig";
import DemandesAjout from "./DemandesAjout";
import DemandesProcedure from "./DemandesProcedure";
import DemandesEtapes from "./DemandesEtapes";
import DemandesDevis from "./DemandesDevis";
import Settings from "./Settings";
import BootstrapTables from "./tables/BootstrapTables";
import Etapeprocedure from "./examples/Etapeprocedure";
import Agent from "./Agent/Agent";
// Composants UI
import Accordion from "./components/Accordion";
import Alerts from "./components/Alerts";
import Badges from "./components/Badges";
import Breadcrumbs from "./components/Breadcrumbs";
import Buttons from "./components/Buttons";
import Forms from "./components/Forms";
import Modals from "./components/Modals";
import Navs from "./components/Navs";
import Navbars from "./components/Navbars";
import Pagination from "./components/Pagination";
import Popovers from "./components/Popovers";
import Progress from "./components/Progress";
import Tables from "./components/Tables";
import Tabs from "./components/Tabs";
import Tooltips from "./components/Tooltips";
import Toasts from "./components/Toasts";

// Pages documentation
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";
import DocsQuickStart from "./documentation/DocsQuickStart";
import DocsLicense from "./documentation/DocsLicense";
import DocsFolderStructure from "./documentation/DocsFolderStructure";
import DocsBuild from "./documentation/DocsBuild";
import DocsChangelog from "./documentation/DocsChangelog";

import { Routes } from "../routes";
import PrivateRoute from "../components/PrivateRoute";

const RouteWithLoader = ({ children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Preloader show={!loaded} />
      {loaded && children}
    </>
  );
};

const RouteWithSidebar = ({ children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const [showSettings, setShowSettings] = useState(() => {
    return localStorage.getItem("settingsVisible") !== "false";
  });

  const toggleSettings = () => {
    setShowSettings((prev) => {
      localStorage.setItem("settingsVisible", !prev);
      return !prev;
    });
  };

  return (
    <>
      <Preloader show={!loaded} />
      {loaded && (
        <>
          <Sidebar />
          <main className="content">
            <Navbar />
            {children}
            <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
          </main>
        </>
      )}
    </>
  );
};

export default function AppRoutes() {
  return (
    <ReactRoutes>
      {/* Auth pages */}
      <Route path={Routes.Presentation.path} element={<RouteWithLoader><Presentation /></RouteWithLoader>} />
      <Route path={Routes.Signin.path} element={<RouteWithLoader><Signin /></RouteWithLoader>} />
      <Route path={Routes.Signup.path} element={<RouteWithLoader><Signup /></RouteWithLoader>} />
      <Route path={Routes.ForgotPassword.path} element={<RouteWithLoader><ForgotPassword /></RouteWithLoader>} />
      <Route path={Routes.ResetPassword.path} element={<RouteWithLoader><ResetPassword /></RouteWithLoader>} />
      <Route path={Routes.Lock.path} element={<RouteWithLoader><Lock /></RouteWithLoader>} />
      <Route path={Routes.NotFound.path} element={<RouteWithLoader><NotFoundPage /></RouteWithLoader>} />
      <Route path={Routes.ServerError.path} element={<RouteWithLoader><ServerError /></RouteWithLoader>} />

      {/* Dashboard pages — protégées par PrivateRoute (session vérifiée par l'API) */}
      <Route element={<PrivateRoute />}>
      <Route path={Routes.DashboardOverview.path} element={<RouteWithSidebar><DashboardOverview /></RouteWithSidebar>} />
      <Route path={Routes.Upgrade.path} element={<RouteWithSidebar><Upgrade /></RouteWithSidebar>} />
      <Route path={Routes.Transactions.path} element={<RouteWithSidebar><Transactions /></RouteWithSidebar>} />
      <Route path={Routes.Arrondissement.path} element={<RouteWithSidebar><Arrondissement /></RouteWithSidebar>} />
      <Route path={Routes.Villes.path} element={<RouteWithSidebar><Cities /></RouteWithSidebar>} />
      <Route path={Routes.Categories.path} element={<RouteWithSidebar><Categories /></RouteWithSidebar>} />
      <Route path={Routes.AccessConfig.path} element={<RouteWithSidebar><AccessConfig /></RouteWithSidebar>} />
      <Route path={Routes.Demandes.path} element={<RouteWithSidebar><DemandesAjout /></RouteWithSidebar>} />
      <Route path={Routes.DemandesProcedure.path} element={<RouteWithSidebar><DemandesProcedure /></RouteWithSidebar>} />
      <Route path={Routes.DemandesEtapes.path} element={<RouteWithSidebar><DemandesEtapes /></RouteWithSidebar>} />
      <Route path={Routes.Settings.path} element={<RouteWithSidebar><Settings /></RouteWithSidebar>} />
      <Route path={Routes.BootstrapTables.path} element={<RouteWithSidebar><BootstrapTables /></RouteWithSidebar>} />
      <Route path={Routes.Etapeprocedure.path} element={<RouteWithSidebar><Etapeprocedure /></RouteWithSidebar>} />
      <Route path={Routes.Agent.path} element={<RouteWithSidebar><Agent /></RouteWithSidebar>} />
      <Route path={Routes.DemandesDevis.path} element={<RouteWithSidebar><DemandesDevis /></RouteWithSidebar>} />
      {/* Components */}
      <Route path={Routes.Accordions.path} element={<RouteWithSidebar><Accordion /></RouteWithSidebar>} />
      <Route path={Routes.Alerts.path} element={<RouteWithSidebar><Alerts /></RouteWithSidebar>} />
      <Route path={Routes.Badges.path} element={<RouteWithSidebar><Badges /></RouteWithSidebar>} />
      <Route path={Routes.Breadcrumbs.path} element={<RouteWithSidebar><Breadcrumbs /></RouteWithSidebar>} />
      <Route path={Routes.Buttons.path} element={<RouteWithSidebar><Buttons /></RouteWithSidebar>} />
      <Route path={Routes.Forms.path} element={<RouteWithSidebar><Forms /></RouteWithSidebar>} />
      <Route path={Routes.Modals.path} element={<RouteWithSidebar><Modals /></RouteWithSidebar>} />
      <Route path={Routes.Navs.path} element={<RouteWithSidebar><Navs /></RouteWithSidebar>} />
      <Route path={Routes.Navbars.path} element={<RouteWithSidebar><Navbars /></RouteWithSidebar>} />
      <Route path={Routes.Pagination.path} element={<RouteWithSidebar><Pagination /></RouteWithSidebar>} />
      <Route path={Routes.Popovers.path} element={<RouteWithSidebar><Popovers /></RouteWithSidebar>} />
      <Route path={Routes.Progress.path} element={<RouteWithSidebar><Progress /></RouteWithSidebar>} />
      <Route path={Routes.Tables.path} element={<RouteWithSidebar><Tables /></RouteWithSidebar>} />
      <Route path={Routes.Tabs.path} element={<RouteWithSidebar><Tabs /></RouteWithSidebar>} />
      <Route path={Routes.Tooltips.path} element={<RouteWithSidebar><Tooltips /></RouteWithSidebar>} />
      <Route path={Routes.Toasts.path} element={<RouteWithSidebar><Toasts /></RouteWithSidebar>} />

      {/* Documentation */}
      <Route path={Routes.DocsOverview.path} element={<RouteWithSidebar><DocsOverview /></RouteWithSidebar>} />
      <Route path={Routes.DocsDownload.path} element={<RouteWithSidebar><DocsDownload /></RouteWithSidebar>} />
      <Route path={Routes.DocsQuickStart.path} element={<RouteWithSidebar><DocsQuickStart /></RouteWithSidebar>} />
      <Route path={Routes.DocsLicense.path} element={<RouteWithSidebar><DocsLicense /></RouteWithSidebar>} />
      <Route path={Routes.DocsFolderStructure.path} element={<RouteWithSidebar><DocsFolderStructure /></RouteWithSidebar>} />
      <Route path={Routes.DocsBuild.path} element={<RouteWithSidebar><DocsBuild /></RouteWithSidebar>} />
      <Route path={Routes.DocsChangelog.path} element={<RouteWithSidebar><DocsChangelog /></RouteWithSidebar>} />
      </Route>

      {/* Not found fallback */}
      <Route path="*" element={<Navigate to={Routes.NotFound.path} replace />} />
    </ReactRoutes>
  );
}
